import React, { useEffect, useState, useRef } from 'react';
import SearchInput from '../../components/shared/SearchInput';
import { serviceOrganization } from '../../services/service-organization';
import { CustomAvatar } from '../../components/shared';
import AddOrganization from './add-organization';
import Pagination from '@mui/material/Pagination';
import { debounce } from 'lodash';
import OptionsMenu from '../../components/shared/OptionsMenu/OptionsMenu';
import './styles.css';
import { serviceSalesOrgApplication } from '../../services/salesOrgApplicationService';
import AppLayout from '../../components/layout/app-layout/app-layout';
import BtButton from '../bt-button/bt-button';
import { getDaysLeft } from '../../util';
import DataTable from '../../components/shared/DataTable/DataTable';
import Tabs from '../../components/shared/Tabs';
import Selector from '../../components/shared/Selector/Selector';
import { withRouter } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

function Organization({ history, location }) {
  const [t] = useTranslation('organizations');
  const [orgData, setOrgData] = useState([]);
  const [loader, setLoader] = useState(false);
  const [showAddOrgModal, setShowAddOrgModal] = useState(false);
  const [addOrgModalKey, setAddOrgModalKey] = useState(0);
  const [editOrgData, setEditOrgData] = useState(null);
  const [searchVal, setSearchVal] = useState('');
  const [filterSearch, setFilterSearch] = useState({
    searchFilter: '',
    draw: '1',
    start: 0,
    activeProduct: null,
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedTab, setSelectedTab] = useState(0);
  const [applicationCount, setApplicationCount] = useState(0);
  const delayedQuery = useRef(debounce(q => handleChange(q, 'searchFilter'), 500)).current;
  const activeProducts = [
    {
      label: t('product.purchasing', 'Inköp'),
      value: 'PURCHASING',
    },
    {
      label: t('product.sales', 'Försäljning'),
      value: 'SALES',
    },
  ];

  useEffect(() => {
    setSelectedTab(location.pathname.endsWith('inactive') ? 1 : 0);
  }, [location.pathname]);

  useEffect(() => {
    fetchOrgData();
  }, [selectedTab, currentPage, filterSearch]);

  useEffect(() => {
    if (showAddOrgModal) {
      setAddOrgModalKey(addOrgModalKey + 1);
    }
  }, [showAddOrgModal]);

  useEffect(() => {
    (async () => {
      const response = await serviceSalesOrgApplication.listPendingSignedApplications();
      setApplicationCount(response.length);
    })();
  });

  const fetchOrgData = async () => {
    setLoader(true);
    const data = await serviceOrganization.getOrganizations({isActive: selectedTab == 0, ...filterSearch });
    setLoader(false);
    if (data) {
      setOrgData(data);
    }
  };

  const handleChange = (value, type) => {
    let filters = {...filterSearch};
    filters[type] = value;
    if ((type == 'filtersData' && (value.type || value.status)) || type == 'searchFilter' || type == 'activeProduct') {
      filters.start = 0;
      setCurrentPage(1);
    }
    setFilterSearch(filters);
  };

  const getColumns = () => {
    return [
      { title: t('table.header.orgName', 'Namn'), render: row => renderOrgName(row) },
      { title: t('table.header.agreementTime', 'Avtalstid'), render: row => renderAgreementTime(row) },
      { title: t('table.header.status', 'Status'), render: row => renderOrgStatus(row) },
      { title: t('table.header.products', 'Produkter'), render: row => renderOrgProducts(row) },
      { title: t('table.header.customerType', 'Kundtyp'), field: 'customerType' },
      {
        title: '',
        render: row => (
          <OptionsMenu
            style={{ display: 'flex', justifyContent: 'flex-end' }}
            options={[{ label: t('table.row.tools.edit', 'Redigera'), action: () => onEdit(row) }]}
          />
        ),
      },
    ];
  };

  const renderAgreementTime = row => {
    if (!row.expiry) return;
    const daysLeft = getDaysLeft(row.expiry);
    return (
      <div className="ac-generic-td-agreementTime">
        <div>{new Date(row.expiry).toDateString()}</div>
        {isNaN(+daysLeft) ? (
          <div>{daysLeft}</div>
        ) : (
          <div style={{ color: daysLeft < 0 ? 'var(--red-700)' : 'var(--green-700)' }}>{
            t(
              'table.row.agreementTime.daysLeft',
              '{{count}} dagar kvar',
              {
                context: +daysLeft < 0 ? 'expired' : null,
                count: Math.abs(+daysLeft)
              }
            )
          }</div>
        )}
      </div>
    );
  };

  const renderOrgStatus = row => (
    <div className={`ac-generic-td-status ${row.isActive}`}>
      {row.isActive ? t('status.active', 'Aktiv') : t('status.inactive', 'Inaktiv')}
    </div>
  );

  const renderOrgProducts = row => {
    return (
      <div style={{ display: 'flex', gap: '8px' }} className="ac-generic-td-status">
        {row?.activeProducts?.includes('PURCHASING') && <span className="badge purchasing">{t('product.purchasing', 'Inköp')}</span>}
        {row?.activeProducts?.includes('SALES') && <span className="badge sales">{t('product.sales', 'Sälj')}</span>}
      </div>
    )
  }

  const renderOrgName = row => (
    <div className="ac-generic-td-name">
      <CustomAvatar alt={row.name} />
      <div className="td-name-inner-wrapper">
        <div>{row.name}</div>
        <div>{row.email}</div>
      </div>
    </div>
  );

  const onEdit = orgData => {
    setEditOrgData(orgData);
    toggleShowAddOrgModal();
  };

  const toggleShowAddOrgModal = () => {
    setShowAddOrgModal(!showAddOrgModal);
  };

  const saveOrg = async () => {
    await fetchOrgData();
    toggleShowAddOrgModal();
  };

  const onPageChanged = currentPage => {
    setCurrentPage(currentPage);
    const pageOffset = (currentPage - 1) * 10;
    handleChange(pageOffset, 'start');
  };

  const onSearch = e => {
    const searchKey = e.target.value;
    setSearchVal(searchKey);
    delayedQuery(searchKey);
  };

  const onChangeTab = tab => {
    const base = location.pathname.split("/").slice(0, 2).join("/");
    if (tab == 0) history.push(base + "/organizations");
    else if (tab == 1) history.push(base + "/organizations/inactive");
    else if (tab == 2) history.push(base + "/organizations/applications");
    setSelectedTab(tab);
    onPageChanged(1);
  }

  return (
    <AppLayout title={t('pageTitle', 'Organisationer')}>
      <div style={{ padding: "32px" }}>
        <div className="tab-container">
          <Tabs
            tabs={[
              t('tabs.active', 'Aktiva organisationer'),
              t('tabs.inactive', 'Inaktiva organisationer'),
              t('tabs.applications', 'Ansökningar ({{count}})', { count: applicationCount })
            ]}
            selected={selectedTab}
            onChange={onChangeTab}
          />
          <div className="tab-actions">
            <BtButton onClick={() => {
                setEditOrgData(null);
                toggleShowAddOrgModal();
              }}>
              {t('button.add', 'Lägg till organisation')}
            </BtButton>
          </div>
        </div>

        <div style={{ display: 'flex', gap: '8px' }} className="ac-generic-table-sub-head">
          <SearchInput placeholder={t('placeholder.search', 'Sök organisation')} value={searchVal} onChange={onSearch} />
          <Selector
            className='activeProductSelectorOrganizationList'
            styles={{ control: (baseStyles) => ({
              ...baseStyles,
              height: '38px'
            }), }}
            isClearable
            isSearchable={false}
            placeholder={t('placeholder.products', 'Produkter')}
            options={activeProducts}
            value={filterSearch?.activeProduct ? activeProducts.find(x => x.value === filterSearch?.activeProduct) : null}
            onChange={(e) => handleChange(e ? e.value : null, 'activeProduct')}
          />
        </div>

        <DataTable
          isLoading={loader}
          columns={getColumns()}
          data={orgData.data || []}
        />

        <div className="sup-pagination org-pagination">
          {orgData.recordsFiltered > 0 && (
            <Pagination
              count={Math.ceil(orgData.recordsFiltered/10)}
              onChange={(_, value) => onPageChanged(value)}
              page={currentPage}
            />
          )}
        </div>

        <AddOrganization
          show={showAddOrgModal}
          onModalHide={toggleShowAddOrgModal}
          saveOrg={saveOrg}
          editOrgData={editOrgData}
          key={addOrgModalKey}
        />
      </div>
    </AppLayout>
  );
}

export default withRouter(Organization);
